import $ from 'jquery';

/**
 * Initialize Semantic UI dropdowns with WAI-ARIA Combobox pattern
 * Supports both regular dropdowns and link-based navigation dropdowns
 * Implements proper ARIA states, aria-activedescendant, and focus management
 * @param {string} context - Optional selector context to limit initialization scope
 */
export function initWaiAriaDropdown(context) {
  const selector = context ? context + ' .ui.dropdown.wai-aria' : '.ui.dropdown.wai-aria';

  $(selector).each(function () {
    const $dropdown = $(this);

    // Skip if already initialized
    if ($dropdown.data('wai-aria-initialized')) {
      return;
    }
    $dropdown.data('wai-aria-initialized', true);

    let currentValue = $dropdown.data('current-value');
    let $menu = $dropdown.find('[role="listbox"]');
    let $options = $menu.find('[role="option"]');
    let currentIndex = -1;

    // Create live region for announcements
    const $liveRegion = $('<div>')
      .attr({
        role: 'status',
        'aria-live': 'assertive',
        'aria-atomic': 'true',
      })
      .addClass('not-show-wai')
      .appendTo('body');

    /**
     * Update aria-activedescendant and visual focus
     */
    function updateActiveDescendant($option) {
      if ($option && $option.length) {
        let optionId = $option.attr('id');
        if (optionId) {
          $dropdown.attr('aria-activedescendant', optionId);

          // Update visual focus - remove from all, add to current
          $options.removeClass('selected');
          $option.addClass('selected');

          // Update current index
          currentIndex = $options.index($option);
        }
      } else {
        $dropdown.removeAttr('aria-activedescendant');
        currentIndex = -1;
      }
    }

    /**
     * Update aria-selected on all options
     */
    function updateSelectedOption(selectedValue) {
      $options.each(function () {
        let $opt = $(this);
        let optValue = $opt.attr('data-value');
        $opt.attr('aria-selected', optValue === selectedValue ? 'true' : 'false');
      });
    }

    /**
     * Announce to screen reader
     * Clear first, then set after delay to force screen reader to announce
     */
    function updateAriaLive(message) {
      $liveRegion.text('');
      setTimeout(function () {
        $liveRegion.text(message);
      }, 50);
    }

    /**
     * Navigate to option by index
     */
    function navigateToIndex(index) {
      if (index >= 0 && index < $options.length) {
        let $option = $options.eq(index);
        updateActiveDescendant($option);

        // Announce the option
        let text = $option.text().trim();
        let isSelected = $option.attr('aria-selected') === 'true';
        updateAriaLive(text + (isSelected ? ', selected' : ', not selected'));
      }
    }

    $dropdown.dropdown({
      selectOnKeydown: false,
      forceSelection: false,
      keys: {
        enter: false,
      },
      action: function (text, value, element) {
        if (value === currentValue) {
          $dropdown.dropdown('hide');
          return;
        }

        // Announce the selection before action
        let selectedText = $(element).text().trim();
        updateAriaLive(selectedText + ' selected');

        let href = $(element).attr('href');
        if (href) {
          // Frontend: navigate to href after brief delay for announcement
          setTimeout(function () {
            window.location.href = href;
          }, 100);
        } else {
          // Backend: trigger native click to let backend handler run
          setTimeout(function () {
            $dropdown.dropdown('hide');
            element.click();
          }, 100);
        }
      },
      // WAI-ARIA Combobox: Handle show event
      onShow: function () {
        $dropdown.attr('aria-expanded', 'true');

        // Set aria-activedescendant to currently selected option
        const $selected = $options.filter('[aria-selected="true"]');
        if ($selected.length === 0) {
          $selected = $options.first();
        }
        updateActiveDescendant($selected);

        // Announce the currently focused option
        const text = $selected.text().trim();
        updateAriaLive(text + ', selected');
      },
      // WAI-ARIA Combobox: Handle hide event
      onHide: function () {
        $dropdown.attr('aria-expanded', 'false');
        $dropdown.removeAttr('aria-activedescendant');
        $options.removeClass('selected');
        currentIndex = -1;

        // Ensure focus returns to the combobox
        $dropdown.trigger('focus');
      },
      // WAI-ARIA Combobox: Update aria-selected when selection changes
      onChange: function (value, text) {
        updateSelectedOption(value);
        currentValue = value;

        // Update the displayed text
        let $textSpan = $dropdown.find('.text');
        if ($textSpan.length) {
          $textSpan.text(text);
        }
      },
    });

    if (currentValue) {
      $dropdown.dropdown('set selected', currentValue);
      updateSelectedOption(currentValue);
    }

    // WAI-ARIA Combobox: Keyboard support
    if ($dropdown.attr('tabindex') !== undefined) {
      $dropdown.on('keydown', function (e) {
        if (e.key === 'Tab') {
          $dropdown.dropdown('hide');
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const isOpen = $dropdown.attr('aria-expanded') === 'true';

        // Enter or Space opens the dropdown
        if ((e.key === 'Enter' || e.key === ' ') && !isOpen) {
          $dropdown.dropdown('show');
          return;
        }

        // Escape closes the dropdown
        if (e.key === 'Escape' && isOpen) {
          $dropdown.dropdown('hide');
          return;
        }

        // Arrow keys navigation when dropdown is open
        if (isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          let newIndex = currentIndex;
          if (e.key === 'ArrowDown') {
            newIndex = (currentIndex + 1) % $options.length;
          } else if (e.key === 'ArrowUp') {
            newIndex = currentIndex - 1;
            if (newIndex < 0) {
              newIndex = $options.length - 1;
            }
          }

          navigateToIndex(newIndex);
          return;
        }

        // Enter or Space select the current option
        if (isOpen && (e.key === 'Enter' || e.key === ' ')) {
          if (currentIndex >= 0) {
            let $current = $options.eq(currentIndex);
            $current.trigger('click');
          }
          return;
        }
      });

      // Mouse hover updates aria-activedescendant
      $options.on('mouseenter', function () {
        if ($dropdown.hasClass('active')) {
          updateActiveDescendant($(this));
          currentIndex = $options.index($(this));
        }
      });

      // When combobox receives focus, announce current selection
      $dropdown.on('focus', function () {
        if (!$dropdown.hasClass('active')) {
          let $selected = $options.filter('[aria-selected="true"]');
          if ($selected.length) {
            let text = $selected.text().trim();
            updateAriaLive(text + ', current selection');
          }
        }
      });
    }
  });
}

// Initialize on document ready for frontend
$(function () {
  initWaiAriaDropdown();
});
